/**
 * Floor Plan Service
 *
 * Service for uploading, replacing, and deleting gym floor plan images.
 * Handles Firebase Storage operations and image dimension extraction.
 */

import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFirebaseStorage } from '@/lib/firebase';
import { MIME_TO_EXTENSION } from '@/components/common/ImageDropZone';

/**
 * Maximum floor plan file size (10MB)
 * Floor plans are typically larger than logos and may have high resolution
 */
export const FLOOR_PLAN_MAX_SIZE = 10 * 1024 * 1024;

/**
 * Allowed MIME types for floor plans
 *
 * SECURITY NOTE: SVG (image/svg+xml) is intentionally excluded because SVG files
 * can contain embedded JavaScript that executes when rendered in a browser,
 * creating XSS vulnerabilities. Only raster image formats are allowed.
 */
export const FLOOR_PLAN_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * Result from validating a floor plan image
 */
export interface FloorPlanValidationResult {
  /** Whether the file passed validation */
  valid: boolean;
  /** Error message if validation failed */
  error?: string;
}

/**
 * Result from uploading a floor plan image
 */
export interface FloorPlanUploadResult {
  /** Download URL for the uploaded image */
  url: string;
  /** Image width in pixels */
  width: number;
  /** Image height in pixels */
  height: number;
}

/**
 * Progress callback type for upload operations
 */
export type UploadProgressCallback = (progress: number) => void;

/**
 * Validate a floor plan image file before upload
 *
 * Checks:
 * - File type is an allowed image format
 * - File size is within limits
 *
 * @param file - The file to validate
 * @returns Validation result with error message if invalid
 */
export const validateFloorPlanImage = (file: File): FloorPlanValidationResult => {
  // Validate file type
  if (!FLOOR_PLAN_ALLOWED_TYPES.includes(file.type)) {
    const typeList = FLOOR_PLAN_ALLOWED_TYPES.map((t) => t.replace('image/', '')).join(', ');
    return {
      valid: false,
      error: `File must be one of: ${typeList.toUpperCase()}`,
    };
  }

  // Validate file size
  if (file.size > FLOOR_PLAN_MAX_SIZE) {
    const sizeMB = Math.round(FLOOR_PLAN_MAX_SIZE / (1024 * 1024));
    return {
      valid: false,
      error: `File must be less than ${sizeMB}MB`,
    };
  }

  return { valid: true };
};

/**
 * Get the dimensions of an image file
 *
 * Creates an in-memory Image element to read the natural width/height.
 *
 * @param file - The image file to measure
 * @returns Promise resolving to width and height
 * @throws Error if the image fails to load
 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = objectUrl;
  });
};

/**
 * Upload a floor plan image to Firebase Storage
 *
 * Uploads the file with progress tracking and returns the download URL
 * along with the image dimensions for storage in Firestore.
 *
 * @param file - The image file to upload
 * @param gymId - The gym ID for the storage path
 * @param userId - The user ID for metadata
 * @param onProgress - Optional callback for upload progress (0-100)
 * @returns Promise resolving to upload result with URL and dimensions
 * @throws Error if upload fails
 *
 * @example
 * ```typescript
 * const result = await uploadFloorPlanImage(file, gymId, userId, (progress) => {
 *   setUploadProgress(progress);
 * });
 * // result: { url: 'https://...', width: 1920, height: 1080 }
 * ```
 */
export const uploadFloorPlanImage = async (
  file: File,
  gymId: string,
  userId: string,
  onProgress?: UploadProgressCallback
): Promise<FloorPlanUploadResult> => {
  // Get image dimensions first (validates the image can be loaded)
  const dimensions = await getImageDimensions(file);

  // Generate storage path with timestamp to avoid collisions
  const storage = getFirebaseStorage();
  const extension = MIME_TO_EXTENSION[file.type] ?? 'png';
  const timestamp = Date.now();
  const storagePath = `gyms/${gymId}/floor-plan_${timestamp}.${extension}`;
  const storageRef = ref(storage, storagePath);

  // Upload with progress tracking
  const uploadTask = uploadBytesResumable(storageRef, file, {
    contentType: file.type,
    customMetadata: {
      uploadedBy: userId,
      originalName: file.name,
    },
  });

  // Wait for upload to complete
  const url = await new Promise<string>((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        onProgress?.(progress);
      },
      (error) => {
        reject(error);
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadUrl);
        } catch (err) {
          reject(err);
        }
      }
    );
  });

  return {
    url,
    width: dimensions.width,
    height: dimensions.height,
  };
};

/**
 * Delete a floor plan image from Firebase Storage
 *
 * Attempts to delete the file at the given URL. Errors are logged but not thrown,
 * since the URL may be from a different source or already deleted.
 *
 * @param imageUrl - The Firebase Storage URL of the image to delete
 */
export const deleteFloorPlanImage = async (imageUrl: string): Promise<void> => {
  try {
    const storage = getFirebaseStorage();
    const storageRef = ref(storage, imageUrl);
    await deleteObject(storageRef);
  } catch (error) {
    // Log but don't throw - the file may not exist or URL may be external
    console.warn('Could not delete floor plan image from storage:', error);
  }
};
